import { FactoryBillingAutomationWeb } from '@skelloapp/svc-billing-automation-sdk';
import {
  VUE_APP_SVC_BILLING_AUTOMATION_PUBLIC_URL,
  VUE_APP_SVC_BILLING_AUTOMATION_URL,
} from '@/shared/config';
import {
  mockQuote,
  mockContractChangeQuote,
  mockRolloutSplitShopsAndEmployees,
  mockRolloutQuote,
  mockDealroomV2MassMarket,
  mockDealroomV2Enterprise,
  mockDealroomV2EnterpriseSeasonal,
  mockQuoteOrganisation,
  mockQuoteOrganisationSeasonal,
  mockQuoteSplitShops,
  mockQuoteSplitShopsSeasonal,
  mockQuoteSplitShopsAndEmployees,
} from '@/shared/mocks';

class BillingAutomationClient {
  constructor() {
    this.factory = this.getFactoryByEnv();
  }

  getEnv() {
    return localStorage.getItem('salesforceEnv') || 'dev05';
  }

  getFactoryByEnv() {
    // The SDK sends the public routes to publicUrl (alb-external) and the others to url
    return FactoryBillingAutomationWeb.create({
      publicUrl: VUE_APP_SVC_BILLING_AUTOMATION_PUBLIC_URL,
      url: VUE_APP_SVC_BILLING_AUTOMATION_URL,
    }, {
      clientSource: 'skelloOnboarding:web',
      // Default timeout is 5000, some calls take more than that
      timeout: 10000,
      apiVersion: this.getEnv() === 'integneo' ? 'v2026' : undefined,
      // The bearer token comes from the Salesforce magic-link query param `?t=...`.
      tokenProvider: async () => new URLSearchParams(window.location.search).get('t') || '',
    });
  }

  updateFactoryByEnv(env) {
    localStorage.setItem('salesforceEnv', env);
    location.reload();
  }

  async getQuoteById(id) {
    // Uncomment one of the next lines to test with mock quotes:
    // return new Promise(resolve => { resolve({ payload: mockQuote }); });
    // return new Promise(resolve => { resolve({ payload: mockContractChangeQuote }); });
    // return new Promise(resolve => { resolve({ payload: mockRolloutQuote }); });
    // return new Promise(resolve => { resolve({ payload: mockDealroomV2MassMarket }); });
    // return new Promise(resolve => { resolve({ payload: mockDealroomV2Enterprise }); });
    // return new Promise(resolve => { resolve({ payload: mockDealroomV2EnterpriseSeasonal }); });
    // return new Promise(resolve => { resolve({ payload: mockQuoteOrganisation }); });
    // return new Promise(resolve => { resolve({ payload: mockQuoteOrganisationSeasonal }); });
    // return new Promise(resolve => { resolve({ payload: mockQuoteSplitShops }); });
    // return new Promise(resolve => { resolve({ payload: mockQuoteSplitShopsSeasonal }); });
    // return new Promise(resolve => { resolve({ payload: mockQuoteSplitShopsAndEmployees }); });
    // return new Promise(resolve => { resolve({ payload: mockRolloutSplitShopsAndEmployees }); });
    return this.factory.quote.getById(this.encodeId(id));
  }

  async getCompanyRegistrationsByName(companyName) {
    return this.factory.companyRegistration.getCompanyRegistrationsByName(companyName);
  }

  async getCompanyRegistrationsByShopRegistrationNumber(shopRegistrationNumber) {
    return this.factory.companyRegistration
      .getCompanyRegistrationsByShopRegistrationNumber(shopRegistrationNumber);
  }

  async getCompanyRegistrationsByOrganisationRegistrationNumber(organisationRegistrationNumber) {
    return this.factory.companyRegistration
      .getCompanyRegistrationsByOrganisationRegistrationNumber(organisationRegistrationNumber);
  }

  // options.quoteId is sent as the X-Quote-Id header, which proves the caller holds a quote
  // owning this Chargebee customer; the service decodes it like the quote ids in paths
  encodePaymentOptions(options) {
    return options?.quoteId ? { ...options, quoteId: this.encodeId(options.quoteId) } : options;
  }

  async createCheckoutByChargebeeIdAndBillingEntity(chargebeeId, entity, options) {
    return this.factory.checkout.createByChargebeeIdAndBillingEntity(
      chargebeeId,
      entity,
      this.encodePaymentOptions(options),
    );
  }

  async createChargebeeSource(
    chargebeeId,
    city,
    country,
    email,
    firstName,
    iban,
    lastName,
    line1,
    postalCode,
    options) {
    return this.factory.chargebee.createSource(
      chargebeeId,
      city,
      country,
      email,
      firstName,
      iban,
      lastName,
      line1,
      postalCode,
      this.encodePaymentOptions(options),
    );
  }

  async validateQuote(id, quote) {
    // Uncomment next line to move to step 2 with mock quote:
    // return new Promise(resolve => { resolve({ payload: mockQuote }); });
    return this.factory.quote.validateQuote(this.encodeId(id), quote);
  }

  async updateQuoteBillingInfos(id, quote) {
    return this.factory.quote.updateQuoteBillingInfos(this.encodeId(id), quote);
  }

  async updateQuoteShopBillingInfos(id, quote) {
    return this.factory.quote.updateQuoteShopBillingInfos(this.encodeId(id), quote);
  }

  async createOrganisation(id, password) {
    return this.factory.organisation.create(this.encodeId(id), password);
  }

  async updateOrganisation({ quoteId, sendEmail, parentClusterNodeId }) {
    return this.factory.organisation.update({
      quoteId: this.encodeId(quoteId),
      sendEmail,
      parentClusterNodeId,
    });
  }

  // SF uses encrypted IDs that contain % characters
  // So we encode to base64 to avoid URL issues
  encodeId(id) {
    return btoa(id);
  }

  async startContractChange(quoteId) {
    return this.factory.organisation.startContractChange(this.encodeId(quoteId));
  }
}

export const billingAutomationClient = new BillingAutomationClient();

export default billingAutomationClient;
